import React, { useEffect, useState } from 'react';
import { Bell, Mail, MessageSquare, Plus, Edit2, Trash2, Check, X, Save } from 'lucide-react';
import { callWFAPI } from '../utils/helper';
interface NotificationTemplate {
  id: string;
  title: string;
  subject: string;
  body: string;
  variables: string[];
  enabled: boolean;
}
interface GeneralNotification {
  id: string;
  email: string;
  phone: string;
}
interface TechnicianWithNotification {
  id: string;
  name: string;
  email: string;
  phone: string;
}
export function NotificationTemplates() {
  const [generalNotifications, setGeneralNotifications] = useState<GeneralNotification[]>([]);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<Partial<NotificationTemplate>>({});
  const [savingTemplate, setSavingTemplate] = useState<string | null>(null);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [newRecipientPhone, setNewRecipientPhone] = useState('');
  const [addingRecipient, setAddingRecipient] = useState(false);
  useEffect(() => {
    loadTemplates();
    loadRecipients();
  }, []);
  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Rental_Notification_Templates'
      };
      const resp = await callWFAPI(0, 'zohoCreator', data);
      const loadedTemplates: NotificationTemplate[] = resp.map((item: any) => ({
        id: item.ID,
        title: item.Title || '',
        subject: item.Subject_field || '',
        body: item.Body || '',
        variables: item.Variables ? item.Variables.split('\n').filter((v: string) => v.trim()) : [],
        enabled: item.Enabled === 'true'
      }));
      setTemplates(loadedTemplates);
    } catch (error) {
      console.error('Error loading notification templates:', error);
      alert('Failed to load notification templates. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const formatPhoneNumber = (phone: string): string => {
    if (!phone) return '';
    // Remove all non-digit characters except '+'
    let cleaned = phone.replace(/[^\d+]/g, '');
    // If it starts with '+', keep it; otherwise prepend '+1'
    if (!cleaned.startsWith('+')) {
      cleaned = '+1' + cleaned;
    }
    return cleaned;
  };
  const loadRecipients = async () => {
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Rental_Notification_Recipients'
      };
      const resp = await callWFAPI(0, 'zohoCreator', data);
      const loadedRecipients: GeneralNotification[] = resp.map((item: any) => ({
        id: item.ID,
        email: item.Email || '',
        phone: item.Phone_Number || ''
      }));
      // If no recipients in database, use default values
      if (loadedRecipients.length === 0) {
        setGeneralNotifications([{
          id: 'default-1',
          email: 'shane@tkdscore.com',
          phone: '+15172940315'
        }, {
          id: 'default-2',
          email: 'janice@truescore.com',
          phone: '+14088284937'
        }]);
      } else {
        setGeneralNotifications(loadedRecipients);
      }
    } catch (error) {
      console.error('Error loading notification recipients:', error);
      // On error, use default values
      setGeneralNotifications([{
        id: 'default-1',
        email: 'shane@tkdscore.com',
        phone: '+15172940315'
      }, {
        id: 'default-2',
        email: 'janice@truescore.com',
        phone: '+14088284937'
      }]);
    }
  };
  const addGeneralNotification = async () => {
    if (!newRecipientEmail.trim() && !newRecipientPhone.trim()) {
      alert('Please enter at least an email or phone number');
      return;
    }
    setAddingRecipient(true);
    try {
      const formattedPhone = formatPhoneNumber(newRecipientPhone);
      const data = {
        appname: 'tkd-score',
        formname: 'Rental_Notification_Recipient',
        params: JSON.stringify({
          Email: newRecipientEmail.trim(),
          Phone_Number: formattedPhone
        })
      };
      const newRecipient = await callWFAPI(0, 'zohoCreator', data);
      setGeneralNotifications([...generalNotifications, {
        id: newRecipient.ID,
        email: newRecipientEmail.trim(),
        phone: formattedPhone
      }]);
      setNewRecipientEmail('');
      setNewRecipientPhone('');
    } catch (error) {
      console.error('Error adding recipient:', error);
      alert('Failed to add recipient. Please try again.');
    } finally {
      setAddingRecipient(false);
    }
  };
  const updateGeneralNotification = (index: number, field: 'email' | 'phone', value: string) => {
    const updated = [...generalNotifications];
    updated[index][field] = value;
    setGeneralNotifications(updated);
  };
  const removeGeneralNotification = async (id: string) => {
    if (!window.confirm('Are you sure you want to remove this recipient?')) {
      return;
    }
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Rental_Notification_Recipients',
        id: id,
        delete: 1
      };
      await callWFAPI(0, 'zohoCreator', data);
      setGeneralNotifications(generalNotifications.filter(n => n.id !== id));
    } catch (error) {
      console.error('Error deleting recipient:', error);
      alert('Failed to delete recipient. Please try again.');
    }
  };
  const startEditingTemplate = (template: NotificationTemplate) => {
    setEditingTemplate(template.id);
    setEditingValues({
      ...template
    });
  };
  const cancelEditingTemplate = () => {
    setEditingTemplate(null);
    setEditingValues({});
  };
  const saveTemplateEdit = async () => {
    if (!editingTemplate || !editingValues) return;
    setSavingTemplate(editingTemplate);
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Rental_Notification_Templates',
        id: editingTemplate,
        params: JSON.stringify({
          Subject_field: editingValues.subject,
          Body: editingValues.body
        })
      };
      await callWFAPI(0, 'zohoCreator', data);
      // Update local state
      setTemplates(templates.map(t => t.id === editingTemplate ? {
        ...t,
        subject: editingValues.subject || t.subject,
        body: editingValues.body || t.body
      } : t));
      setEditingTemplate(null);
      setEditingValues({});
    } catch (error) {
      console.error('Error saving template:', error);
      alert('Failed to save template. Please try again.');
    } finally {
      setSavingTemplate(null);
    }
  };
  const toggleTemplateEnabled = async (template: NotificationTemplate) => {
    const newEnabledState = !template.enabled;
    try {
      const data = {
        appname: 'tkd-score',
        reportname: 'All_Rental_Notification_Templates',
        id: template.id,
        params: JSON.stringify({
          Enabled: newEnabledState ? 'true' : 'false'
        })
      };
      await callWFAPI(0, 'zohoCreator', data);
      // Update local state
      setTemplates(templates.map(t => t.id === template.id ? {
        ...t,
        enabled: newEnabledState
      } : t));
    } catch (error) {
      console.error('Error toggling template:', error);
      alert('Failed to update template status. Please try again.');
    }
  };
  const getTemplateIcon = (title: string) => {
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes('event') || lowerTitle.includes('registration')) {
      return <Bell size={16} className="text-blue-600" data-id="element-1133" />;
    }
    if (lowerTitle.includes('invoice') && lowerTitle.includes('created')) {
      return <Mail size={16} className="text-green-600" data-id="element-1134" />;
    }
    if (lowerTitle.includes('payment')) {
      return <Mail size={16} className="text-yellow-600" data-id="element-1135" />;
    }
    if (lowerTitle.includes('checklist') && lowerTitle.includes('complete')) {
      return <Check size={16} className="text-purple-600" data-id="element-1136" />;
    }
    if (lowerTitle.includes('checklist') && lowerTitle.includes('edit')) {
      return <Edit2 size={16} className="text-orange-600" data-id="element-1137" />;
    }
    return <Bell size={16} data-id="element-1138" />;
  };
  if (loading) {
    return <div className="flex items-center justify-center py-12" data-id="element-1139">
        <div className="text-center" data-id="element-1140">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" data-id="element-1141"></div>
          <p className="text-slate-600" data-id="element-1142">Loading notification templates...</p>
        </div>
      </div>;
  }
  return <div className="space-y-8" data-id="element-1143">
      {/* General Notifications */}
      <div data-id="element-1144">
        <div className="mb-4" data-id="element-1145">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2" data-id="element-1146">
            General Notifications
          </h2>
          <p className="text-sm text-slate-600" data-id="element-1147">
            Configure email and phone numbers to receive all system
            notifications
          </p>
        </div>

        <div className="bg-slate-50 rounded-lg p-4 sm:p-6" data-id="element-1148">
          <div className="space-y-4" data-id="element-1149">
            {generalNotifications.map(notification => <div key={notification.id} className="flex flex-col sm:flex-row gap-3 items-start" data-id="element-1150">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3 w-full" data-id="element-1151">
                  <div data-id="element-1152">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1153">
                      Email Address
                    </label>
                    <div className="px-3 py-2 border border-slate-300 rounded-lg bg-slate-100 text-slate-700" data-id="element-1154">
                      {notification.email || '—'}
                    </div>
                  </div>
                  <div data-id="element-1155">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1156">
                      Phone Number (SMS)
                    </label>
                    <div className="px-3 py-2 border border-slate-300 rounded-lg bg-slate-100 text-slate-700" data-id="element-1157">
                      {notification.phone || '—'}
                    </div>
                  </div>
                </div>
                <button onClick={() => removeGeneralNotification(notification.id)} className="self-start sm:self-auto sm:mt-7 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Remove" data-id="element-1158">
                  <Trash2 size={18} data-id="element-1159" />
                </button>
              </div>)}

            {generalNotifications.length === 0 && <div className="text-center py-6 text-slate-500" data-id="element-1160">
                No recipients configured yet
              </div>}

            {/* Add New Recipient Form */}
            <div className="pt-4 border-t border-slate-200" data-id="element-1161">
              <h4 className="text-sm font-medium text-slate-700 mb-3" data-id="element-1162">
                Add New Recipient
              </h4>
              <div className="flex flex-col sm:flex-row gap-3 items-start" data-id="element-1163">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3 w-full" data-id="element-1164">
                  <div data-id="element-1165">
                    <input type="email" value={newRecipientEmail} onChange={e => setNewRecipientEmail(e.target.value)} placeholder="admin@example.com" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1166" />
                  </div>
                  <div data-id="element-1167">
                    <input type="tel" value={newRecipientPhone} onChange={e => setNewRecipientPhone(e.target.value)} placeholder="+1 (555) 123-4567" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1168" />
                  </div>
                </div>
                <button onClick={addGeneralNotification} disabled={addingRecipient || !newRecipientEmail.trim() && !newRecipientPhone.trim()} className="self-start sm:self-auto px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-2" data-id="element-1169">
                  {addingRecipient ? <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" data-id="element-1170"></div>
                      Adding...
                    </> : <>
                      <Plus size={16} data-id="element-1171" />
                      Add
                    </>}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Templates */}
      <div data-id="element-1172">
        <div className="mb-4" data-id="element-1173">
          <h2 className="text-lg sm:text-xl font-semibold text-slate-800 mb-2" data-id="element-1174">
            Notification Templates
          </h2>
          <p className="text-sm text-slate-600" data-id="element-1175">
            Customize email and SMS templates for different events. Use
            variables like {'{{event_name}}'} to insert dynamic content.
          </p>
        </div>

        <div className="space-y-4" data-id="element-1176">
          {templates.map(template => <div key={template.id} className={`border rounded-lg overflow-hidden transition-colors ${template.enabled ? 'border-slate-200 bg-white' : 'border-slate-200 bg-slate-50 opacity-60'}`} data-id="element-1177">
              <div className="p-4" data-id="element-1178">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3" data-id="element-1179">
                  <div className="flex items-center gap-3" data-id="element-1180">
                    {getTemplateIcon(template.title)}
                    <h3 className="font-semibold text-slate-800 break-words" data-id="element-1181">
                      {template.title}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2 self-start sm:self-auto" data-id="element-1182">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-1183">
                      <input type="checkbox" checked={template.enabled} onChange={() => toggleTemplateEnabled(template)} className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500" data-id="element-1184" />
                      <span className="text-sm text-slate-600" data-id="element-1185">Enabled</span>
                    </label>
                    {editingTemplate === template.id ? <div className="flex gap-1" data-id="element-1186">
                        <button onClick={saveTemplateEdit} disabled={savingTemplate === template.id} className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50" title="Save" data-id="element-1187">
                          {savingTemplate === template.id ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" data-id="element-1188"></div> : <Check size={18} data-id="element-1189" />}
                        </button>
                        <button onClick={cancelEditingTemplate} disabled={savingTemplate === template.id} className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50" title="Cancel" data-id="element-1190">
                          <X size={18} data-id="element-1191" />
                        </button>
                      </div> : <button onClick={() => startEditingTemplate(template)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit" data-id="element-1192">
                        <Edit2 size={18} data-id="element-1193" />
                      </button>}
                  </div>
                </div>

                {editingTemplate === template.id ? <div className="space-y-3" data-id="element-1194">
                    <div data-id="element-1195">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1196">
                        Subject Line
                      </label>
                      <input type="text" value={editingValues.subject || ''} onChange={e => setEditingValues({
                  ...editingValues,
                  subject: e.target.value
                })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-1197" />
                    </div>
                    <div data-id="element-1198">
                      <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1199">
                        Message Body
                      </label>
                      <textarea value={editingValues.body || ''} onChange={e => setEditingValues({
                  ...editingValues,
                  body: e.target.value
                })} rows={8} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm resize-none" data-id="element-1200" />
                    </div>
                    {template.variables.length > 0 && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3" data-id="element-1201">
                        <p className="text-xs font-medium text-blue-800 mb-2" data-id="element-1202">
                          Available Variables:
                        </p>
                        <div className="flex flex-wrap gap-2" data-id="element-1203">
                          {template.variables.map(variable => <code key={variable} className="text-xs bg-white px-2 py-1 rounded border border-blue-300 text-blue-700" data-id="element-1204">
                              {variable}
                            </code>)}
                        </div>
                      </div>}
                  </div> : <div className="space-y-2" data-id="element-1205">
                    <div data-id="element-1206">
                      <p className="text-xs font-medium text-slate-500 mb-1" data-id="element-1207">
                        Subject:
                      </p>
                      <p className="text-sm text-slate-700" data-id="element-1208">
                        {template.subject}
                      </p>
                    </div>
                    <div data-id="element-1209">
                      <p className="text-xs font-medium text-slate-500 mb-1" data-id="element-1210">
                        Body:
                      </p>
                      <p className="text-sm text-slate-700 whitespace-pre-line" data-id="element-1211">
                        {template.body}
                      </p>
                    </div>
                  </div>}
              </div>
            </div>)}
        </div>

        {templates.length === 0 && <div className="text-center py-12" data-id="element-1212">
            <Mail size={48} className="text-slate-300 mx-auto mb-4" data-id="element-1213" />
            <p className="text-slate-600" data-id="element-1214">No notification templates found</p>
          </div>}
      </div>
    </div>;
}